import { Box, Card, Typography } from "@mui/material";
import React from "react";
import { alpha } from "@mui/material/styles";
import { COLORS, WARNING } from "../../theme/theme-options";

type Tone = "brand" | "danger" | "warning";

const toneStyles: Record<
  Tone,
  { badgeBg: string; badgeColor: string; subColor: string }
> = {
  brand: {
    badgeBg: alpha(COLORS.primary[500], 0.1),
    badgeColor: COLORS.primary[400],
    subColor: alpha(COLORS.grey[300], 0.8),
  },
  danger: {
    badgeBg: alpha(COLORS.error[500], 0.1),
    badgeColor: COLORS.error[500],
    subColor: COLORS.error[400],
  },
  warning: {
    badgeBg: alpha(WARNING[500], 0.1),
    badgeColor: WARNING[400],
    subColor: WARNING[400],
  },
};

export type MetricCardProps = {
  label: string;
  value: number;
  suffix?: string;
  note?: string;
  icon: React.ReactNode;
  tone?: Tone;
};

export const MetricCard = ({
  label,
  value,
  suffix = "/100",
  note,
  icon,
  tone = "brand",
}: MetricCardProps) => {
  const t = toneStyles[tone];

  return (
    <Card variant="glass" sx={{ p: 2 }}>
      <Box display="flex" flexDirection="column" gap={1}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: t.badgeBg,
              color: t.badgeColor,
              "& .MuiSvgIcon-root": { fontSize: 16 },
            }}
          >
            {icon}
          </Box>
          <Typography variant="body2" sx={{ color: alpha(COLORS.grey[300], 0.75) }}>
            {label}
          </Typography>
        </Box>

        <Box display="flex" alignItems="baseline" gap={1}>
          <Typography sx={{ fontSize: 24, fontWeight: 700, color: COLORS.grey[100] }}>
            {value}
          </Typography>
          {suffix.length > 0 && (
            <Typography variant="body2" sx={{ color: alpha(COLORS.grey[500], 0.9) }}>
              {suffix}
            </Typography>
          )}
          {note && (
            <Typography
              variant="body2"
              sx={{ color: t.subColor, ml: 0.5, whiteSpace: "nowrap" }}
            >
              {note}
            </Typography>
          )}
        </Box>
      </Box>
    </Card>
  );
};
